import {
  dateAt,
  type KeyDate,
  type KeyDateRef,
  type MonthIndex,
  type YearMonth,
} from '@finsim/model';
import { useUnit } from 'effector-react';
import { Link2, Link2Off } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMonthYear } from '../../../shared/lib/format';
import { useLocale } from '../../../shared/lib/i18n/use-locale';
import {
  EditOnly,
  IconButton,
  IntegerField,
  MonthField,
  Select,
  Tooltip,
} from '../../../shared/ui';
import { $keyDates } from '../model';

export interface KeyDateMonthFieldProps {
  value: MonthIndex;
  /** The key date the month is tied to, if any. */
  anchor: KeyDateRef | undefined;
  /** `month` is already the key date + offset when `anchor` is set. */
  onCommit: (month: MonthIndex, anchor: KeyDateRef | undefined) => void;
  /** Calendar month of month index 0. */
  start: YearMonth;
  min?: number;
  max: number;
  id?: string | undefined;
  'aria-label': string;
  className?: string;
}

/** The key date on `month`, or else the nearest one. */
function nearest(keyDates: readonly KeyDate[], month: number): KeyDate | undefined {
  let best: KeyDate | undefined;
  for (const k of keyDates) {
    if (!best || Math.abs(k.month - month) < Math.abs(best.month - month)) best = k;
  }
  return best;
}

/**
 * A month picker that can tie the month to one of the project's key dates, plus or minus some
 * months. Without key dates (or outside a project) it is a plain `MonthField`.
 */
export function KeyDateMonthField({
  value,
  anchor,
  onCommit,
  start,
  min = 0,
  max,
  id,
  className,
  'aria-label': label,
}: KeyDateMonthFieldProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const keyDates = useUnit($keyDates);
  const tiedTo = anchor && keyDates.find((k) => k.id === anchor.keyDate);

  if (!anchor || !tiedTo) {
    const field = (
      <MonthField
        id={id}
        aria-label={label}
        {...(keyDates.length > 0
          ? { className: 'min-w-0 flex-1' }
          : className === undefined
            ? {}
            : { className })}
        value={value}
        start={start}
        min={min}
        max={max}
        onCommit={(month) => {
          onCommit(month, undefined);
        }}
      />
    );
    if (keyDates.length === 0) return field;
    const tieLabel = t('keyDates.tie', { field: label });
    return (
      <div className={`flex items-start gap-1.5 ${className ?? ''}`}>
        {field}
        <EditOnly>
          <Tooltip content={tieLabel}>
            <IconButton
              size="md"
              label={tieLabel}
              onClick={() => {
                const k = nearest(keyDates, value);
                if (k) onCommit(value, { keyDate: k.id, offset: value - k.month });
              }}
            >
              <Link2 />
            </IconButton>
          </Tooltip>
        </EditOnly>
      </div>
    );
  }

  const untieLabel = t('keyDates.untie', { field: label });
  const date = formatMonthYear(dateAt(start, value), locale, 'long');
  return (
    <div className={`space-y-1 ${className ?? ''}`}>
      <div className="flex items-start gap-1.5">
        <Select
          {...(id === undefined ? {} : { id })}
          className="min-w-0 flex-1"
          aria-label={`${label} — ${t('keyDates.keyDate')}`}
          value={tiedTo.id}
          options={keyDates.map((k) => {
            // A key date that would put this month out of range can't be picked.
            const month = k.month + anchor.offset;
            return { value: k.id, label: k.name, disabled: month < min || month > max };
          })}
          onValueChange={(next) => {
            const k = keyDates.find((x) => x.id === next);
            if (k) onCommit((k.month + anchor.offset) as MonthIndex, { ...anchor, keyDate: k.id });
          }}
        />
        <IntegerField
          className="w-20"
          aria-label={`${label} — ${t('keyDates.offset')}`}
          value={anchor.offset}
          min={min - tiedTo.month}
          max={max - tiedTo.month}
          adornment={t('keyDates.monthsShort')}
          onCommit={(offset) => {
            onCommit((tiedTo.month + offset) as MonthIndex, { ...anchor, offset });
          }}
        />
        <EditOnly>
          <Tooltip content={untieLabel}>
            <IconButton
              size="md"
              label={untieLabel}
              onClick={() => {
                onCommit(value, undefined);
              }}
            >
              <Link2Off />
            </IconButton>
          </Tooltip>
        </EditOnly>
      </div>
      <p className="text-xs text-text-3">{date}</p>
    </div>
  );
}
